import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import styles from '../styles/NavigationBar.module.css';
import { IconButton } from './Button';
import { useUser } from '@clerk/clerk-react';
import StreakDisplay from './StreakDisplay';
import StreakInfoModal from './StreakInfoModal';
import LearnedTermsDisplay from './LearnedTermsDisplay';
import StellineDisplay from './StellineDisplay';
import LearnedTermsInfoModal from './LearnedTermsInfoModal';
import StellineInfoModal from './StellineInfoModal';
import DrawerMenu from './DrawerMenu';
import { menuItems } from '../config/menuConfig';
import { useGlobalModal } from '../contexts/ModalContext';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useResourceCelebration } from '../contexts/ResourceCelebrationContext';
import useResourceReceiveAnimation from '../hooks/useResourceReceiveAnimation';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import AnnouncementBanner from './AnnouncementBanner';
import { useRecommendedGame } from '../hooks/useRecommendedGame';
import { getRandomUnstartedGame, getCurrentDate } from '../util';
import { useGameStatuses } from '../contexts/GameStatusesContext';
import { trackAuthCtaClicked, trackUiElementClicked } from '../analytics';
import { GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES } from '../ads/googleInterstitialAttributes';

const NavigationBar = ({
  onBack,
  backIcon = "fas fa-chevron-left",
  hideBackButton = false,
  minimal = false,
}) => {
  const [scrolled, setScrolled] = useState(() =>{
    if (typeof window === 'undefined') return false;
    return window.scrollY > 0;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, user } = useUser();
  const { openModal } = useGlobalModal();
  const { firstName: userFirstName } = useUserProfile();
  const { registerTarget, targetImpactVersions } = useResourceCelebration();
  const profileButtonRef = useRef(null);
  const gettonciniImpactVersion = targetImpactVersions.gettoncini || 0;
  const isReceivingGettoncini = useResourceReceiveAnimation(gettonciniImpactVersion);
  const hasProfileButton = !minimal && !!isSignedIn && !!user;
  const hasEverAuthenticated = useHasEverAuthenticated();

  useEffect(() => {
    if (!hasProfileButton) return undefined;
    return registerTarget('gettoncini', profileButtonRef.current);
  }, [registerTarget, hasProfileButton]);

  // Quick Play section: recommendation and random game
  const currentDate = getCurrentDate();
  const { recommendation, isLoading: isRecommendationLoading } = useRecommendedGame(currentDate, null, {
    // Keep navbar lightweight by default, but fetch immediately when drawer opens
    // so "Consigliato" doesn't stay disabled just because recommendation data wasn't loaded yet.
    subscribeOnly: !drawerOpen,
    allowBackgroundRevalidateOnSubscribe: true,
    priority: drawerOpen ? 'high' : 'low',
  });
  const { getGameStatus } = useGameStatuses();
  
  // Build dynamic menu items with Quick Play section
  const dynamicMenuItems = useMemo(() => {
    const quickPlaySection = {
      type: 'section',
      label: 'Gioca Subito',
      items: [
        recommendation?.type === 'game' 
          ? { label: 'Consigliato', icon: 'fas fa-star', href: recommendation.path }
          : {
              label: 'Consigliato',
              icon: isRecommendationLoading ? 'fas fa-spinner fa-spin' : 'fas fa-star',
              href: '/',
              disabled: true,
            },
        { 
          label: 'Casuale', 
          icon: 'fas fa-dice',
          onClick: () => {
            const random = getRandomUnstartedGame(currentDate, getGameStatus, { isSignedIn });
            if (random) {
              navigate(random.path);
            }
            // If no unstarted games, do nothing (button will be disabled via recommendation check)
          }
        }
      ]
    };
    
    return [quickPlaySection, { type: 'divider' }, ...menuItems];
  }, [recommendation, isRecommendationLoading, currentDate, getGameStatus, isSignedIn, hasEverAuthenticated, navigate]);
  
  // Determine what to show in the left button slot:
  // - If onBack is provided and not hidden: show back button
  // - If onBack is provided but hidden: show nothing (for overlays with close button elsewhere)
  // - Otherwise: show hamburger menu
  const showBackButton = !minimal && onBack && !hideBackButton;
  const showHamburger = !minimal && !onBack;

  const toggleDrawer = useCallback(() => {
    setDrawerOpen((prev) => {
      const next = !prev;
      trackUiElementClicked({
        source_surface: 'navbar',
        surface: 'navbar',
        element_id: 'hamburger_menu',
        action: next ? 'open' : 'close',
      });
      return next;
    });
  }, []);

  const closeDrawer = useCallback((reason = 'unknown') => {
    setDrawerOpen((prev) => {
      if (!prev) return prev;
      trackUiElementClicked({
        source_surface: 'drawer_menu',
        surface: 'drawer_menu',
        element_id: 'drawer_close',
        action: 'close',
        trigger: reason,
      });
      return false;
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    // Check initial scroll position on mount (for page reload while scrolled)
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBack = () => {
    if (onBack) return onBack();
    navigate('/');
  };

  const handleStreakClick = () => {
    trackUiElementClicked({
      source_surface: 'navbar',
      surface: 'navbar_top_right',
      element_id: 'streak_display',
      action: 'open_modal',
    });
    openModal({
      title: 'La tua serie',
      content: <StreakInfoModal />,
      analytics: {
        source_surface: 'navbar_top_right',
        modal_name: 'streak_info',
        trigger: 'streak_display_click',
      },
    });
  };

  const handleLearnedTermsClick = () => {
    trackUiElementClicked({
      source_surface: 'navbar',
      surface: 'navbar_top_right',
      element_id: 'learned_terms_display',
      action: 'open_modal',
    });
    openModal({
      title: 'Contatore di parole',
      content: <LearnedTermsInfoModal />,
      analytics: {
        source_surface: 'navbar_top_right',
        modal_name: 'learned_terms_info',
        trigger: 'learned_terms_display_click',
      },
    });
  };

  const handleStellineClick = () => {
    trackUiElementClicked({
      source_surface: 'navbar',
      surface: 'navbar_top_right',
      element_id: 'stelline_display',
      action: 'open_modal',
    });
    openModal({
      title: 'Stelline',
      content: <StellineInfoModal />,
      analytics: {
        source_surface: 'navbar_top_right',
        modal_name: 'stelline_info',
        trigger: 'stelline_display_click',
      },
    });
  };

  const handleProfileClick = () => {
    trackUiElementClicked({
      source_surface: 'navbar',
      surface: 'navbar_top_right',
      element_id: 'profile_entry',
      action: 'open_account',
    });
    navigate('/account');
  };

  const handleAuthEntryClick = (ctaKind) => {
    trackUiElementClicked({
      source_surface: 'navbar',
      surface: 'navbar_top_right',
      element_id: `auth_${ctaKind}`,
      action: 'click',
    });
    trackAuthCtaClicked({
      source_surface: 'navbar_desktop',
      gate_reason: 'generic_auth_entry',
      cta_kind: ctaKind,
      next_path: `${location.pathname}${location.search}`,
      is_returning_device: hasEverAuthenticated,
    });
  };

  const signInUrl = useMemo(() => {
    const params = new URLSearchParams();
    const currentPath = `${location.pathname}${location.search}`;
    if (currentPath && currentPath !== '/') {
      params.set('next', currentPath);
    }
    params.set('source', 'navbar_desktop');
    params.set('gate_reason', 'generic_auth_entry');
    return `/signin?${params.toString()}`;
  }, [location.pathname, location.search]);

  const signUpUrl = useMemo(() => {
    const params = new URLSearchParams();
    const currentPath = `${location.pathname}${location.search}`;
    if (currentPath && currentPath !== '/') {
      params.set('next', currentPath);
    }
    params.set('source', 'navbar_desktop');
    params.set('gate_reason', 'generic_auth_entry');
    return `/signup?${params.toString()}`;
  }, [location.pathname, location.search]);

  return (
    <>
      <header className={styles.header}>
        {!minimal ? <AnnouncementBanner /> : null}
        <div className={`${styles.navigationBar} ${scrolled ? styles.scrolled : ''} ${minimal ? styles.navigationBarMinimal : ''}`}>
          {/* Left button slot: back button, hamburger menu, or nothing */}
          {showBackButton ? (
            // Back button (when onBack is provided and not hidden)
            typeof backIcon === 'string' ? (
              <IconButton iconClassName={backIcon} onClick={handleBack} className={styles.backButton} />
            ) : (
              <button type="button" onClick={handleBack} className={styles.backButton} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                {backIcon}
              </button>
            )
          ) : showHamburger ? (
            // Hamburger Menu Button (default for normal pages)
            <button 
              type="button"
              className={`${styles.hamburgerButton} ${drawerOpen ? styles.hamburgerOpen : ''}`}
              onClick={toggleDrawer}
              aria-label={drawerOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={drawerOpen}
            >
              <span className={styles.hamburgerLine} />
              <span className={styles.hamburgerLine} />
              <span className={styles.hamburgerLine} />
            </button>
          ) : null}

          {/* Always show brand name that links to home */}
          <Link to="/" className={styles.brandLink} {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}>
            <img src="/gdp_logo_20260111.png" alt="Giochini di Parole" className={styles.brandLogo} />
            <span className={styles.brandName}>Giochini di Parole</span>
          </Link>

          {!minimal ? (
            <div className={styles.navIcons}>
              <StreakDisplay onClick={handleStreakClick} />
              <StellineDisplay onClick={handleStellineClick} />
              <LearnedTermsDisplay onClick={handleLearnedTermsClick} />
              {/* User avatar or profile icon */}
              {isSignedIn && user ? (
                <button
                  ref={profileButtonRef}
                  type="button"
                  onClick={handleProfileClick}
                  className={`${styles.avatarButton} ${isReceivingGettoncini ? styles.receivingGettoncini : ''}`}
                  title="Account"
                  data-resource-target="gettoncini"
                >
                  {userFirstName && <span className={styles.userName}>{userFirstName}</span>}
                  <img src={user.imageUrl} alt="Account" className={styles.avatarImage} />
                </button>
              ) : (
                <>
                  {/* Mobile: show user icon */}
                  <IconButton
                    iconClassName="fas fa-user"
                    onClick={handleProfileClick}
                    className={`${styles.navIcon} ${styles.mobileOnly}`}
                    title="Account"
                  />
                  {/* Desktop: show auth buttons */}
                  <div className={styles.authButtons}>
                    {hasEverAuthenticated ? (
                      /* Returning user - only show sign in */
                      <Link
                        to={signInUrl}
                        className={styles.authButtonPrimary}
                        {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}
                        onClick={() => handleAuthEntryClick('sign_in')}
                      >
                        Accedi
                      </Link>
                    ) : (
                      /* New device - show both options */
                      <>
                        <Link
                          to={signInUrl}
                          className={styles.authButtonSecondary}
                          {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}
                          onClick={() => handleAuthEntryClick('sign_in')}
                        >
                          Accedi
                        </Link>
                        <Link
                          to={signUpUrl}
                          className={styles.authButtonPrimary}
                          {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}
                          onClick={() => handleAuthEntryClick('sign_up')}
                        >
                          Registrati
                        </Link>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      </header>
      {/* Only show drawer when hamburger menu is visible */}
      {showHamburger && (
        <DrawerMenu 
          isOpen={drawerOpen} 
          onClose={closeDrawer} 
          menuItems={dynamicMenuItems}
        />
      )}
    </>
  );
};

export default NavigationBar;
